import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileUploadDropzone } from "@/components/ui/file-upload-dropzone";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Check, X, ArrowRight, Download, Car } from "lucide-react";

const VEHICLE_FIELDS = [
  { key: "name", label: "Name/Bezeichnung", required: true },
  { key: "manufacturer", label: "Hersteller", required: false },
  { key: "vehicle_type", label: "Fahrzeugart", required: false },
  { key: "vin", label: "FIN", required: false },
  { key: "license_plate", label: "Kennzeichen", required: false },
  { key: "tuv_until", label: "TÜV bis", required: false },
  { key: "leased_until", label: "Geleast bis", required: false },
  { key: "dkv_card", label: "DKV Karte", required: false },
  { key: "year_of_manufacture", label: "Baujahr", required: false },
  { key: "first_registration", label: "Erstzulassung", required: false },
  { key: "leasing_rate", label: "Leasing Rate", required: false },
  { key: "insurance_monthly", label: "Versicherung/Monat", required: false },
  { key: "tax_monthly", label: "Steuer", required: false },
  { key: "fuel_monthly", label: "Treibstoff/Strom", required: false },
  { key: "workshop_monthly", label: "Werkstatt", required: false },
  { key: "other_costs_monthly", label: "Sonstige Kosten", required: false },
  { key: "ownership", label: "Besitz", required: false },
  { key: "winter_tires", label: "Winterreifen", required: false },
  { key: "notes", label: "Notizen", required: false },
];

interface VehicleCSVImportProps {
  onImportComplete?: () => void;
}

export function VehicleCSVImport({ onImportComplete }: VehicleCSVImportProps) {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState<"upload" | "mapping" | "preview" | "done">("upload");
  const [importResults, setImportResults] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });

  const parseCSV = (text: string): string[][] => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    return lines.map(line => {
      const values: string[] = [];
      let current = "";
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if ((char === "," || char === ";") && !inQuotes) {
          values.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      return values;
    });
  };

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith(".csv")) {
      toast.error("Bitte wählen Sie eine CSV-Datei aus");
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCSV(text);
      
      if (parsed.length < 2) {
        toast.error("Die CSV-Datei muss mindestens eine Kopfzeile und eine Datenzeile enthalten");
        return;
      }

      setCsvHeaders(parsed[0]);
      setCsvData(parsed.slice(1));
      setColumnMapping({});
      setStep("mapping");
    };
    reader.readAsText(file);
  };

  const handleMappingChange = (csvColumn: string, vehicleField: string) => {
    setColumnMapping(prev => {
      const newMapping = { ...prev };
      // Remove any existing mapping to this vehicle field
      Object.keys(newMapping).forEach(key => {
        if (newMapping[key] === vehicleField) {
          delete newMapping[key];
        }
      });
      if (vehicleField && vehicleField !== "ignore") {
        newMapping[csvColumn] = vehicleField;
      }
      return newMapping;
    });
  };

  const getMappedData = () => {
    return csvData.map(row => {
      const vehicle: Record<string, any> = {};
      csvHeaders.forEach((header, index) => {
        const fieldKey = columnMapping[header];
        if (fieldKey && row[index]) {
          let value: any = row[index];
          
          // Convert dates
          if ((fieldKey === "tuv_until" || fieldKey === "leased_until" || fieldKey === "first_registration") && value) {
            // Try to parse German date format (DD.MM.YYYY)
            const germanDate = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
            if (germanDate) {
              value = `${germanDate[3]}-${germanDate[2].padStart(2, "0")}-${germanDate[1].padStart(2, "0")}`;
            }
          }
          
          // Convert numbers
          if (["leasing_rate", "insurance_monthly", "tax_monthly", "fuel_monthly", "workshop_monthly", "other_costs_monthly"].includes(fieldKey) && value) {
            value = parseFloat(value.replace(",", ".")) || 0;
          }
          
          // Convert year
          if (fieldKey === "year_of_manufacture" && value) {
            value = parseInt(value) || null;
          }
          
          // Convert boolean
          if (fieldKey === "winter_tires") {
            value = value.toLowerCase() === "ja" || value.toLowerCase() === "true" || value === "1";
          }
          
          vehicle[fieldKey] = value;
        }
      });
      return vehicle;
    });
  };

  const canProceedToPreview = () => {
    const mappedFields = Object.values(columnMapping);
    return mappedFields.includes("name");
  };

  const handleImport = async () => {
    if (!user) return;
    
    setImporting(true);
    const mappedData = getMappedData();
    let success = 0;
    let failed = 0;

    for (const vehicle of mappedData) {
      if (!vehicle.name) {
        failed++;
        continue;
      }

      try {
        const insertData = {
          name: vehicle.name as string,
          manufacturer: vehicle.manufacturer || null,
          vehicle_type: vehicle.vehicle_type || null,
          vin: vehicle.vin || null,
          license_plate: vehicle.license_plate || null,
          tuv_until: vehicle.tuv_until || null,
          leased_until: vehicle.leased_until || null,
          dkv_card: vehicle.dkv_card || null,
          year_of_manufacture: vehicle.year_of_manufacture || null,
          first_registration: vehicle.first_registration || null,
          leasing_rate: vehicle.leasing_rate || 0,
          insurance_monthly: vehicle.insurance_monthly || 0,
          tax_monthly: vehicle.tax_monthly || 0,
          fuel_monthly: vehicle.fuel_monthly || 0,
          workshop_monthly: vehicle.workshop_monthly || 0,
          other_costs_monthly: vehicle.other_costs_monthly || 0,
          ownership: vehicle.ownership || null,
          winter_tires: vehicle.winter_tires || false,
          notes: vehicle.notes || null,
          created_by: user.id,
        };
        
        const { error } = await supabase.from("vehicles").insert(insertData);

        if (error) {
          console.error("Error importing vehicle:", error);
          failed++;
        } else {
          success++;
        }
      } catch (error) {
        console.error("Error importing vehicle:", error);
        failed++;
      }
    }

    setImportResults({ success, failed });
    setStep("done");
    setImporting(false);

    if (success > 0) {
      toast.success(`${success} Fahrzeuge erfolgreich importiert`);
      onImportComplete?.();
    }
    if (failed > 0) {
      toast.error(`${failed} Fahrzeuge konnten nicht importiert werden`);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setColumnMapping({});
    setStep("upload");
    setImportResults({ success: 0, failed: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const downloadCSVTemplate = () => {
    const headers = VEHICLE_FIELDS.map(f => f.label).join(";");
    const exampleRow = [
      "Firmenfahrzeug 1",
      "VW",
      "PKW",
      "WVWZZZ1JZWW000001",
      "B-AB 1234",
      "15.06.2025",
      "31.12.2026",
      "DKV-123456",
      "2023",
      "15.01.2023",
      "450,00",
      "120,00",
      "30,00",
      "200,00",
      "50,00",
      "25,00",
      "Leasing",
      "ja",
      "Beispielfahrzeug",
    ].join(";");

    const csvContent = `${headers}\n${exampleRow}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "fahrzeuge_vorlage.csv";
    link.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="h-5 w-5" />
          Fahrzeuge CSV Import
        </CardTitle>
        <CardDescription>
          Importieren Sie Fahrzeugdaten aus einer CSV-Datei mit Spalten-Zuordnung
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {step === "upload" && (
          <div className="space-y-4">
            <FileUploadDropzone
              accept=".csv"
              onFileSelect={handleFileSelect}
              selectedFile={selectedFile}
              onClear={() => setSelectedFile(null)}
              title="CSV-Datei hochladen"
              description="Ziehen Sie eine CSV-Datei hierher oder klicken Sie zum Auswählen. Die erste Zeile sollte die Spaltenüberschriften enthalten."
              acceptedFormats={["CSV"]}
              supportedFields={VEHICLE_FIELDS.map(f => ({ label: f.label, required: f.required }))}
            />
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={downloadCSVTemplate}>
                <Download className="mr-2 h-4 w-4" />
                CSV-Vorlage herunterladen
              </Button>
            </div>
          </div>
        )}

        {step === "mapping" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Ordnen Sie die CSV-Spalten den Fahrzeugfeldern zu
              </p>
              <Button variant="outline" size="sm" onClick={resetImport}>
                Zurück
              </Button>
            </div>
            
            <ScrollArea className="h-[400px] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CSV-Spalte</TableHead>
                    <TableHead></TableHead>
                    <TableHead>Fahrzeugfeld</TableHead>
                    <TableHead>Beispieldaten</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvHeaders.map((header, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{header}</TableCell>
                      <TableCell>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={columnMapping[header] || ""}
                          onValueChange={(value) => handleMappingChange(header, value)}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Feld auswählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ignore">-- Ignorieren --</SelectItem>
                            {VEHICLE_FIELDS.map(field => (
                              <SelectItem 
                                key={field.key} 
                                value={field.key}
                                disabled={Object.values(columnMapping).includes(field.key) && columnMapping[header] !== field.key}
                              >
                                {field.label}{field.required && " *"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {csvData[0]?.[index] || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            <div className="flex items-center justify-between">
              <p className="text-sm">
                {csvData.length} Datensätze gefunden
              </p>
              <Button 
                onClick={() => setStep("preview")}
                disabled={!canProceedToPreview()}
              >
                Vorschau anzeigen
              </Button>
            </div>
            {!canProceedToPreview() && (
              <p className="text-sm text-destructive">
                Bitte ordnen Sie mindestens das Pflichtfeld "Name/Bezeichnung" zu.
              </p>
            )}
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Überprüfen Sie die Daten vor dem Import
              </p>
              <Button variant="outline" size="sm" onClick={() => setStep("mapping")}>
                Zurück zur Zuordnung
              </Button>
            </div>

            <ScrollArea className="h-[400px] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    {VEHICLE_FIELDS.filter(f => Object.values(columnMapping).includes(f.key)).map(field => (
                      <TableHead key={field.key}>{field.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getMappedData().slice(0, 50).map((vehicle, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      {VEHICLE_FIELDS.filter(f => Object.values(columnMapping).includes(f.key)).map(field => (
                        <TableCell key={field.key} className="max-w-[150px] truncate">
                          {typeof vehicle[field.key] === "boolean" 
                            ? (vehicle[field.key] ? "Ja" : "Nein")
                            : (vehicle[field.key] || "-")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {csvData.length > 50 && (
              <p className="text-sm text-muted-foreground">
                Zeige erste 50 von {csvData.length} Datensätzen
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={resetImport}>
                Abbrechen
              </Button>
              <Button onClick={handleImport} disabled={importing}>
                {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {csvData.length} Fahrzeuge importieren
              </Button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="text-center py-8 space-y-4">
            <div className="flex justify-center gap-8">
              <div className="text-center">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-green-600">{importResults.success}</p>
                <p className="text-sm text-muted-foreground">Erfolgreich</p>
              </div>
              {importResults.failed > 0 && (
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-2">
                    <X className="h-8 w-8 text-red-600" />
                  </div>
                  <p className="text-2xl font-bold text-red-600">{importResults.failed}</p>
                  <p className="text-sm text-muted-foreground">Fehlgeschlagen</p>
                </div>
              )}
            </div>
            <Button onClick={resetImport}>
              Weiteren Import starten
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
